import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Users,
  MapPin,
  CurrencyDollar,
  Storefront,
  Trash,
  Camera,
  EnvelopeSimple,
  Phone,
  PhoneOutgoing,
  PhoneIncoming,
  X,
  Path,
  CalendarBlank,
} from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";

export default function AdminSalespersonDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("visits");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: "", id: "", name: "" });
  const [deleting, setDeleting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [calls, setCalls] = useState([]);
  const [photoViewer, setPhotoViewer] = useState({ open: false, photo: null, visit: null });
  const [travelSummary, setTravelSummary] = useState(null);
  const [travelDate, setTravelDate] = useState(new Date().toISOString().split("T")[0]);
  const [customerDetail, setCustomerDetail] = useState({ open: false, customer: null, visits: [], calls: [], sales: [], loading: false });
  const [clearingGPS, setClearingGPS] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchSalespersonData(token);
  }, [userId, navigate]);

  const fetchSalespersonData = async (token) => {
    try {
      const [detailRes, customersRes, callsRes, travelRes] = await Promise.all([
        axios.get(`${API}/admin/salesperson/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/salesperson/${userId}/customers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/salesperson/${userId}/calls`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/travel-summary/${userId}?date=${travelDate}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setData(detailRes.data);
      setCustomers(customersRes.data);
      setCalls(callsRes.data);
      setTravelSummary(travelRes.data);
    } catch (error) {
      toast.error("Failed to load salesperson data");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const fetchTravelSummary = async (date) => {
    const token = localStorage.getItem("adminToken");
    try {
      const res = await axios.get(`${API}/admin/travel-summary/${userId}?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTravelSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch travel summary");
    }
  };

  const handleTravelDateChange = (e) => {
    const newDate = e.target.value;
    setTravelDate(newDate);
    fetchTravelSummary(newDate);
  };

  const handleDelete = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      toast.error("Not authenticated. Please login again.");
      return;
    }
    if (!deleteDialog.type || !deleteDialog.id) {
      toast.error("Invalid delete request");
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/${deleteDialog.type}/${deleteDialog.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${deleteDialog.type} deleted successfully`);
      setDeleteDialog({ open: false, type: "", id: "", name: "" });
      fetchSalespersonData(token);
    } catch (error) {
      console.error("Delete error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to delete";
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  const openCustomerDetail = async (customer) => {
    const token = localStorage.getItem("adminToken");
    setCustomerDetail({ open: true, customer, visits: [], calls: [], sales: [], loading: true });
    
    try {
      const response = await axios.get(`${API}/admin/customer/${customer.id}/activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerDetail(prev => ({
        ...prev,
        visits: response.data.visits || [],
        calls: response.data.calls || [],
        sales: response.data.sales || [],
        loading: false
      }));
    } catch (error) {
      toast.error("Failed to load customer activity");
      setCustomerDetail(prev => ({ ...prev, loading: false }));
    }
  };

  const clearVisitGPS = async (visitId) => {
    const token = localStorage.getItem("adminToken");
    setClearingGPS(visitId);
    try {
      await axios.put(`${API}/admin/visit/${visitId}/gps`, 
        { clear_gps: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("GPS data cleared");
      fetchSalespersonData(token);
    } catch (error) {
      toast.error("Failed to clear GPS data");
    } finally {
      setClearingGPS(null);
    }
  };

  const getPhotoUrl = (path) => {
    const token = localStorage.getItem("adminToken");
    return `${API}/files/${path}?auth=${token}`;
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMM d, yyyy 'at' h:mm a") + " IST";
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0055FF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-salesperson-detail">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 -ml-2 text-[#9CA3AF] hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <p className="text-[#9CA3AF] text-sm">Salesperson</p>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>
              {data?.user?.name}
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* User Info */}
        <div className="card-flat p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-full bg-[#DBEAFE] flex items-center justify-center">
              <span className="text-2xl font-bold text-[#0055FF]">
                {data?.user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-bold text-lg text-[#0A0A0A]">{data?.user?.name}</p>
              <div className="flex items-center gap-1 text-[#4B5563]">
                <EnvelopeSimple size={16} />
                <span className="text-sm">{data?.user?.email}</span>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-5 gap-2">
            <div className="text-center p-2 bg-[#F3F4F6] rounded-lg">
              <Storefront size={18} className="mx-auto text-[#0055FF] mb-1" weight="fill" />
              <p className="font-bold text-[#0A0A0A] text-sm">{data?.stats?.customer_count}</p>
              <p className="text-xs text-[#9CA3AF]">Customers</p>
            </div>
            <div className="text-center p-2 bg-[#F3F4F6] rounded-lg">
              <MapPin size={18} className="mx-auto text-[#10B981] mb-1" weight="fill" />
              <p className="font-bold text-[#0A0A0A] text-sm">{data?.stats?.visit_count}</p>
              <p className="text-xs text-[#9CA3AF]">Visits</p>
            </div>
            <div className="text-center p-2 bg-[#F3F4F6] rounded-lg">
              <Phone size={18} className="mx-auto text-[#8B5CF6] mb-1" weight="fill" />
              <p className="font-bold text-[#0A0A0A] text-sm">{data?.stats?.call_count || 0}</p>
              <p className="text-xs text-[#9CA3AF]">Calls</p>
            </div>
            <div className="text-center p-2 bg-[#F3F4F6] rounded-lg">
              <CurrencyDollar size={18} className="mx-auto text-[#F59E0B] mb-1" weight="fill" />
              <p className="font-bold text-[#0A0A0A] text-sm">${data?.stats?.total_sales?.toLocaleString()}</p>
              <p className="text-xs text-[#9CA3AF]">Sales</p>
            </div>
            <div className="text-center p-2 bg-[#F3F4F6] rounded-lg">
              <p className="font-bold text-[#0A0A0A] text-lg">{data?.stats?.sales_count}</p>
              <p className="text-xs text-[#9CA3AF]">Orders</p>
            </div>
          </div>
        </div>

        {/* Travel Summary */}
        <div className="card-flat p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Path size={20} className="text-[#0055FF]" weight="fill" />
              <h3 className="font-bold text-[#0A0A0A]">Daily Travel</h3>
            </div>
            <Input
              type="date"
              value={travelDate}
              onChange={handleTravelDateChange}
              className="w-auto text-sm"
              data-testid="travel-date-input"
            />
          </div>
          {travelSummary ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center p-3 bg-[#DBEAFE] rounded-lg">
                  <p className="font-bold text-[#0055FF] text-xl">{travelSummary.total_visits}</p>
                  <p className="text-xs text-[#4B5563]">Total Visits</p>
                </div>
                <div className="text-center p-3 bg-[#D1FAE5] rounded-lg">
                  <p className="font-bold text-[#059669] text-xl">{travelSummary.visits_with_gps}</p>
                  <p className="text-xs text-[#4B5563]">With GPS</p>
                </div>
                <div className="text-center p-3 bg-[#FEF3C7] rounded-lg">
                  <p className="font-bold text-[#D97706] text-xl">{travelSummary.total_distance_km} km</p>
                  <p className="text-xs text-[#4B5563]">Distance</p>
                </div>
              </div>
              {travelSummary.total_visits > 0 && travelSummary.visits_with_gps < travelSummary.total_visits && (
                <div className="p-2 bg-[#FEF3C7] rounded-lg text-xs text-[#92400E] flex items-center gap-2">
                  <MapPin size={14} />
                  <span>
                    {travelSummary.total_visits - travelSummary.visits_with_gps} of {travelSummary.total_visits} visits missing GPS data. 
                    Distance only calculated between GPS-enabled visits.
                  </span>
                </div>
              )}
              {travelSummary.total_visits > 0 && travelSummary.visits_with_gps === travelSummary.total_visits && (
                <div className="p-2 bg-[#D1FAE5] rounded-lg text-xs text-[#065F46] flex items-center gap-2">
                  <MapPin size={14} weight="fill" />
                  <span>All visits have GPS data</span>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-[#9CA3AF] text-center">No travel data available</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {["visits", "calls", "sales", "customers"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-3 rounded-full font-medium text-xs transition-colors ${
                activeTab === tab
                  ? "bg-[#0A0A0A] text-white"
                  : "bg-white border border-[#E5E7EB] text-[#4B5563]"
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "visits" && (
          <div className="card-flat overflow-hidden">
            {data?.recent_visits?.length > 0 ? (
              data.recent_visits.map((visit, index) => (
                <div
                  key={visit.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0"
                  data-testid={`visit-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    {visit.photo_path ? (
                      <button
                        onClick={() => setPhotoViewer({ open: true, photo: getPhotoUrl(visit.photo_path), visit })}
                        className="relative group"
                      >
                        <img
                          src={getPhotoUrl(visit.photo_path)}
                          alt="Visit"
                          className="w-20 h-20 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        />
                        <div className="absolute inset-0 bg-black/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs font-medium">View</span>
                        </div>
                      </button>
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                        <Camera size={24} className="text-[#9CA3AF]" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p 
                        className="font-medium text-[#0055FF] hover:underline cursor-pointer"
                        onClick={() => {
                          const customer = customers.find(c => c.id === visit.customer_id);
                          if (customer) openCustomerDetail(customer);
                        }}
                      >
                        {visit.company_name || visit.customer_name}
                      </p>
                      {visit.company_name && visit.customer_name !== visit.company_name && (
                        <p className="text-sm text-[#4B5563]">{visit.customer_name}</p>
                      )}
                      <p className="text-sm text-[#9CA3AF]">{formatDate(visit.visit_date)}</p>
                      {visit.notes && (
                        <p className="text-sm text-[#9CA3AF] mt-1">{visit.notes}</p>
                      )}
                      {/* GPS Status Indicator */}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {visit.latitude && visit.longitude && 
                         typeof visit.latitude === 'number' && typeof visit.longitude === 'number' &&
                         !isNaN(visit.latitude) && !isNaN(visit.longitude) ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#D1FAE5] text-[#059669] rounded-full">
                            <MapPin size={12} weight="fill" />
                            GPS: {visit.latitude.toFixed(4)}, {visit.longitude.toFixed(4)}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#FEE2E2] text-[#DC2626] rounded-full">
                            <MapPin size={12} />
                            No GPS
                          </span>
                        )}
                        {visit.distance_from_prev !== null && visit.distance_from_prev !== undefined && 
                         typeof visit.distance_from_prev === 'number' && !isNaN(visit.distance_from_prev) && 
                         visit.distance_from_prev > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#DBEAFE] text-[#2563EB] rounded-full">
                            <Path size={12} weight="fill" />
                            +{visit.distance_from_prev.toFixed(2)} km from prev
                          </span>
                        )}
                        {/* Clear GPS button for corrupted or wrong GPS data */}
                        {(visit.latitude || visit.longitude) && (
                          <button
                            onClick={() => clearVisitGPS(visit.id)}
                            disabled={clearingGPS === visit.id}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#FEF3C7] text-[#92400E] rounded-full hover:bg-[#FDE68A] transition-colors"
                            title="Clear GPS data for this visit"
                          >
                            <X size={12} />
                            {clearingGPS === visit.id ? "Clearing..." : "Clear GPS"}
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setDeleteDialog({
                        open: true,
                        type: "visit",
                        id: visit.id,
                        name: `visit to ${visit.company_name || visit.customer_name}`
                      })}
                      className="p-2 text-[#9CA3AF] hover:text-[#EF4444]"
                      data-testid={`delete-visit-${index}`}
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-[#9CA3AF]">No visits recorded</div>
            )}
          </div>
        )}

        {activeTab === "calls" && (
          <div className="card-flat overflow-hidden">
            {calls?.length > 0 ? (
              calls.map((call, index) => (
                <div
                  key={call.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0"
                  data-testid={`call-item-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      call.call_type === "inbound" ? "bg-[#D1FAE5]" : "bg-[#DBEAFE]"
                    }`}>
                      {call.call_type === "inbound" ? (
                        <PhoneIncoming size={20} className="text-[#10B981]" weight="fill" />
                      ) : (
                        <PhoneOutgoing size={20} className="text-[#0055FF]" weight="fill" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p 
                          className="font-medium text-[#0055FF] hover:underline cursor-pointer"
                          onClick={() => {
                            const customer = customers.find(c => c.id === call.customer_id);
                            if (customer) openCustomerDetail(customer);
                          }}
                        >
                          {call.company_name || call.customer_name}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          call.call_type === "inbound" ? "bg-[#D1FAE5] text-[#059669]" : "bg-[#DBEAFE] text-[#2563EB]"
                        }`}>
                          {call.call_type === "inbound" ? "Inbound" : "Outbound"}
                        </span>
                        {call.duration_minutes && (
                          <span className="text-xs px-2 py-0.5 bg-[#F3F4F6] rounded text-[#4B5563]">
                            {call.duration_minutes} min
                          </span>
                        )}
                      </div>
                      {call.company_name && call.customer_name !== call.company_name && (
                        <p className="text-sm text-[#4B5563]">{call.customer_name}</p>
                      )}
                      <p className="text-sm text-[#9CA3AF]">{formatDate(call.call_date)}</p>
                      {call.notes && (
                        <p className="text-sm text-[#9CA3AF] mt-1">{call.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setDeleteDialog({
                        open: true,
                        type: "call",
                        id: call.id,
                        name: `call with ${call.company_name || call.customer_name}`
                      })}
                      className="p-2 text-[#9CA3AF] hover:text-[#EF4444]"
                      data-testid={`delete-call-${index}`}
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-[#9CA3AF]">No calls logged</div>
            )}
          </div>
        )}

        {activeTab === "sales" && (
          <div className="card-flat overflow-hidden">
            {data?.recent_sales?.length > 0 ? (
              data.recent_sales.map((sale, index) => (
                <div
                  key={sale.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0"
                  data-testid={`sale-item-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p 
                        className="font-medium text-[#0055FF] hover:underline cursor-pointer"
                        onClick={() => {
                          const customer = customers.find(c => c.id === sale.customer_id);
                          if (customer) openCustomerDetail(customer);
                        }}
                      >
                        {sale.company_name || sale.customer_name}
                      </p>
                      {sale.company_name && sale.customer_name !== sale.company_name && (
                        <p className="text-sm text-[#4B5563]">{sale.customer_name}</p>
                      )}
                      <p className="text-sm text-[#9CA3AF]">{formatDate(sale.sale_date)}</p>
                      {sale.description && (
                        <p className="text-sm text-[#9CA3AF]">{sale.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-bold text-[#10B981] text-lg">${sale.amount.toLocaleString()}</p>
                      <button
                        onClick={() => setDeleteDialog({
                          open: true,
                          type: "sale",
                          id: sale.id,
                          name: `$${sale.amount} sale to ${sale.company_name || sale.customer_name}`
                        })}
                        className="p-2 text-[#9CA3AF] hover:text-[#EF4444]"
                        data-testid={`delete-sale-${index}`}
                      >
                        <Trash size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-[#9CA3AF]">No sales recorded</div>
            )}
          </div>
        )}

        {activeTab === "customers" && (
          <div className="card-flat overflow-hidden">
            {customers?.length > 0 ? (
              customers.map((customer, index) => (
                <div
                  key={customer.id}
                  className="p-4 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F9FAFB] transition-colors"
                  data-testid={`customer-item-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 flex-1 cursor-pointer"
                      onClick={() => openCustomerDetail(customer)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                        <Storefront size={20} className="text-[#4B5563]" weight="fill" />
                      </div>
                      <div>
                        <p className="font-medium text-[#0055FF] hover:underline">{customer.company_name || customer.name}</p>
                        {customer.company_name && customer.name !== customer.company_name && (
                          <p className="text-sm text-[#4B5563]">{customer.name}</p>
                        )}
                        {!customer.company_name && (customer.phone || customer.email) && (
                          <p className="text-sm text-[#4B5563]">
                            {customer.phone || customer.email}
                          </p>
                        )}
                        {customer.business_type && (
                          <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-[#E5E7EB] rounded text-[#4B5563]">
                            {customer.business_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setDeleteDialog({
                          open: true,
                          type: "customer",
                          id: customer.id,
                          name: customer.company_name || customer.name
                        })}
                        className="p-2 text-[#9CA3AF] hover:text-[#EF4444]"
                        data-testid={`delete-customer-${index}`}
                      >
                        <Trash size={20} />
                      </button>
                    </div>
                  </div>
                  {customer.address && (
                    <p className="text-sm text-[#9CA3AF] mt-2 ml-13">{customer.address}</p>
                  )}
                  {customer.notes && (
                    <p className="text-sm text-[#4B5563] mt-1 ml-13 italic">"{customer.notes}"</p>
                  )}
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-[#9CA3AF]">No customers added by this salesperson</div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.type}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteDialog.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: "", id: "", name: "" })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Dialog */}
      <Dialog open={photoViewer.open} onOpenChange={(open) => setPhotoViewer({ ...photoViewer, open })}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <div className="relative">
            <button
              onClick={() => setPhotoViewer({ open: false, photo: null, visit: null })}
              className="absolute top-3 right-3 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
            >
              <X size={20} />
            </button>
            {photoViewer.photo && (
              <img
                src={photoViewer.photo}
                alt="Visit verification"
                className="w-full max-h-[70vh] object-contain bg-black"
              />
            )}
          </div>
          {photoViewer.visit && (
            <div className="p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg text-[#0A0A0A]">{photoViewer.visit.company_name || photoViewer.visit.customer_name}</h3>
                <span className="badge badge-success">Verified Visit</span>
              </div>
              {photoViewer.visit.company_name && photoViewer.visit.customer_name !== photoViewer.visit.company_name && (
                <p className="text-sm text-[#4B5563] mb-2">Contact: {photoViewer.visit.customer_name}</p>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#9CA3AF]">Date & Time</p>
                  <p className="font-medium text-[#0A0A0A]">{formatDate(photoViewer.visit.visit_date)}</p>
                </div>
                <div>
                  <p className="text-[#9CA3AF]">Salesperson</p>
                  <p className="font-medium text-[#0A0A0A]">{data?.user?.name}</p>
                </div>
              </div>
              {photoViewer.visit.notes && (
                <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
                  <p className="text-[#9CA3AF] text-sm">Notes</p>
                  <p className="text-[#0A0A0A]">{photoViewer.visit.notes}</p>
                </div>
              )}
              <p className="text-xs text-[#9CA3AF] mt-3">
                Photo timestamp is embedded in the image for verification
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={customerDetail.open} onOpenChange={(open) => setCustomerDetail({ ...customerDetail, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Storefront size={24} className="text-[#0055FF]" weight="fill" />
              <div>
                <p>{customerDetail.customer?.company_name || customerDetail.customer?.name}</p>
                {customerDetail.customer?.company_name && customerDetail.customer?.name !== customerDetail.customer?.company_name && (
                  <p className="text-sm font-normal text-[#4B5563]">{customerDetail.customer?.name}</p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {customerDetail.customer && (
            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#F9FAFB] rounded-lg">
                {customerDetail.customer.phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={16} className="text-[#4B5563]" />
                    <span className="text-sm">{customerDetail.customer.phone}</span>
                  </div>
                )}
                {customerDetail.customer.email && (
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple size={16} className="text-[#4B5563]" />
                    <span className="text-sm">{customerDetail.customer.email}</span>
                  </div>
                )}
                {customerDetail.customer.address && (
                  <div className="flex items-center gap-2 col-span-2">
                    <MapPin size={16} className="text-[#4B5563]" />
                    <span className="text-sm">{customerDetail.customer.address}</span>
                  </div>
                )}
                {customerDetail.customer.business_type && (
                  <div className="col-span-2">
                    <span className="text-xs px-2 py-1 bg-[#DBEAFE] text-[#0055FF] rounded">
                      {customerDetail.customer.business_type}
                    </span>
                  </div>
                )}
                {customerDetail.customer.notes && (
                  <div className="col-span-2 pt-2 border-t border-[#E5E7EB]">
                    <p className="text-xs text-[#9CA3AF] mb-1">Notes</p>
                    <p className="text-sm text-[#4B5563] italic">"{customerDetail.customer.notes}"</p>
                  </div>
                )}
              </div>

              {customerDetail.loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0055FF]"></div>
                </div>
              ) : (
                <>
                  {/* Visits */}
                  <div>
                    <h4 className="font-semibold text-[#0A0A0A] mb-2 flex items-center gap-2">
                      <MapPin size={18} className="text-[#10B981]" weight="fill" />
                      Visits ({customerDetail.visits.length})
                    </h4>
                    {customerDetail.visits.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {customerDetail.visits.map((visit, idx) => (
                          <div key={visit.id} className="flex items-center justify-between p-2 bg-[#F3F4F6] rounded text-sm">
                            <div>
                              <p className="font-medium">{formatDate(visit.visit_date || visit.created_at)}</p>
                              {visit.notes && <p className="text-[#9CA3AF] text-xs">{visit.notes}</p>}
                            </div>
                            {visit.photo_path && (
                              <Camera size={16} className="text-[#0055FF]" />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#9CA3AF]">No visits recorded</p>
                    )}
                  </div>

                  {/* Calls */}
                  <div>
                    <h4 className="font-semibold text-[#0A0A0A] mb-2 flex items-center gap-2">
                      <Phone size={18} className="text-[#8B5CF6]" weight="fill" />
                      Calls ({customerDetail.calls.length})
                    </h4>
                    {customerDetail.calls.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {customerDetail.calls.map((call, idx) => (
                          <div key={call.id} className="flex items-center justify-between p-2 bg-[#F3F4F6] rounded text-sm">
                            <div className="flex items-center gap-2">
                              {call.call_type === "inbound" ? (
                                <PhoneIncoming size={14} className="text-[#10B981]" />
                              ) : (
                                <PhoneOutgoing size={14} className="text-[#0055FF]" />
                              )}
                              <div>
                                <p className="font-medium">{formatDate(call.call_date || call.created_at)}</p>
                                {call.notes && <p className="text-[#9CA3AF] text-xs">{call.notes}</p>}
                              </div>
                            </div>
                            {call.duration_minutes && (
                              <span className="text-xs text-[#4B5563]">{call.duration_minutes} min</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#9CA3AF]">No calls recorded</p>
                    )}
                  </div>

                  {/* Sales */}
                  <div>
                    <h4 className="font-semibold text-[#0A0A0A] mb-2 flex items-center gap-2">
                      <CurrencyDollar size={18} className="text-[#F59E0B]" weight="fill" />
                      Sales ({customerDetail.sales.length})
                      {customerDetail.sales.length > 0 && (
                        <span className="ml-auto text-[#10B981] font-bold">
                          Total: ${customerDetail.sales.reduce((sum, s) => sum + (s.amount || 0), 0).toLocaleString()}
                        </span>
                      )}
                    </h4>
                    {customerDetail.sales.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {customerDetail.sales.map((sale, idx) => (
                          <div key={sale.id} className="flex items-center justify-between p-2 bg-[#F3F4F6] rounded text-sm">
                            <div>
                              <p className="font-medium">{formatDate(sale.sale_date || sale.created_at)}</p>
                              {sale.description && <p className="text-[#9CA3AF] text-xs">{sale.description}</p>}
                            </div>
                            <span className="font-bold text-[#10B981]">${sale.amount?.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-[#9CA3AF]">No sales recorded</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
